import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { getPublicSite, startRemix, type AgentImage, type PublicSite } from '../../api/agent';
import { ApiError } from '../../api/client';
import { Screen } from '../../ui';
import Composer from './Composer';
import './studio.css';

/**
 * /studio/remix/:slug — where a site's "Made with Saddle · Make your own" badge leads: the site
 * they just used, and a box for what their version should be. The builder starts from a copy of
 * that site, so "the same, but about my dog" is one sentence.
 */
export default function StudioRemix() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState<PublicSite | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getPublicSite(slug).then((s) => live && setSite(s), () => live && setMissing(true));
    return () => {
      live = false;
    };
  }, [slug]);

  async function remix(message: string, images: AgentImage[]) {
    setBusy(true);
    setProblem(null);
    try {
      const { buildId } = await startRemix(slug, message, images);
      navigate(`/studio/builds/${buildId}`);
    } catch (e) {
      setProblem(
        e instanceof ApiError && e.status === 403
          ? "Making your own version isn't open to your account yet — it's coming soon."
          : e instanceof ApiError ? e.message : 'That did not start. Try again in a moment.',
      );
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <Screen title="Make your own">
        <p className="studio-muted">That site isn't around any more — but you can make something new.</p>
        <button type="button" className="studio-example pressable" onClick={() => navigate('/studio')}>Start from scratch</button>
      </Screen>
    );
  }
  if (!site) return <Screen title="Make your own"><div className="studio-spinner" aria-label="Loading" /></Screen>;

  return (
    <Screen title={`Make your own ${site.title}`}>
      <div className="studio-result">
        <div className="studio-phone">
          <img src={site.shotUrl} alt={`${site.title} on a phone`} />
        </div>
      </div>
      {site.remixable ? (
        <section className="studio-section" aria-labelledby="studio-remix">
          <h2 id="studio-remix" className="studio-h2">What should yours be?</h2>
          <Composer
            placeholder="The same, but about my dog Biscuit…"
            submitLabel="Make mine"
            busy={busy}
            onSubmit={remix}
            examples={['The same, but with my own name and colours', 'Make it about my team', 'Turn it into a version for kids']}
          />
          {problem && <p className="studio-problem" role="alert">{problem}</p>}
        </section>
      ) : (
        <p className="studio-muted">Its owner hasn't made this one remixable — but you can make something new in the studio.</p>
      )}
    </Screen>
  );
}
